import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Typography, Flex } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import {
  SbButton, SbCheckbox, SbTag, SbSpin, SbInput, SbModal,
  SbRow, SbCol, SbNotification, BUTTON_TYPE,
  CloseOutlined, SbTooltip,
} from 'uiCore';
import SbDivider from 'uiCore/SbDivider/SbDivider';
import SbCollapse from 'uiCore/SbCollapse/SbCollapse';
import { getRegistryManagement, saveRegistryManagement } from 'Api/apiService';
import { generateKey, getAvatarInitials, deepCloneState } from 'Utils/orgContextUtils';
import { useDraftState } from 'Hooks/useDraftState';
import { useCollapseState } from '../OrgTechContextHelper/helpers';
import InlineAddInput from '../OrgTechContextHelper/InlineAddInput';
import ExpandCollapseButtons from '../OrgTechContextHelper/ExpandCollapseButtons';
import Permission from 'Components/Permission';
import {
  PageWrapperStyled, FunctionAvatarStyled, InputWrapperStyled, FooterStyled,
} from 'Components/OrgContext/OrgTechContextHelper/orgContextStyles';
import {
  StatsBarStyled, CategorySectionStyled, DeleteIconStyled, BackHeaderStyled,
} from './OrgTechContextSetup.styles';

const TOOLTIP_SELECT_TOOL = "Select this tool to add it to your organisation's technology context. Selected tools are referenced in JD generation.";
const TOOLTIP_SEARCH_TOOLS = "Search for tools and technologies to add to your organisation's tech context.";

const { Text, Title } = Typography;

// Flattens nested API response: function → categories → tools
// Extracts tools and selected keys keyed by category for fast lookup
const flattenMergedContext = (merged) => {
  const toolsByCategory = {};
  const selectedKeysByCategory = {};
  Object.values(merged).forEach((func) => {
    func.categories.forEach((cat) => {
      toolsByCategory[cat.category_key] = cat.tools.map((tool) => ({
        tool_key: tool.tool_key,
        tool_name: tool.tool_name,
        is_custom: tool.is_custom ?? false,
      }));
      selectedKeysByCategory[cat.category_key] = cat.tools
        .filter((tool) => tool.selected)
        .map((tool) => tool.tool_key);
    });
  });
  return { toolsByCategory, selectedKeysByCategory };
};

const OrgTechContextSetup = () => {
  const navigate = useNavigate();
  const [mergedCatalog, setMergedCatalog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [toolsByCategory, setToolsByCategory] = useState({});
  const [selectedKeysByCategory, setSelectedKeysByCategory] = useState({});
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const { getDraft, setDraft, clearDraft, clearAllDrafts } = useDraftState();
  const { expandedKeys, setExpandedKeys, expandAll, collapseAll } = useCollapseState();

  // Apply server data + capture a baseline snapshot so Reset can restore the last saved state.
  const applyServerData = (merged) => {
    const { toolsByCategory: tools, selectedKeysByCategory: selected } = flattenMergedContext(merged);
    setMergedCatalog(merged);
    setToolsByCategory(tools);
    setSelectedKeysByCategory(selected);
    setSavedSnapshot({ tools: deepCloneState(tools), selected: deepCloneState(selected) });
  };

  useEffect(() => {
    const loadConfiguration = async () => {
      try {
        const data = await getRegistryManagement();
        applyServerData(data?.merged_context ?? {});
      } catch {
        setMergedCatalog({});
        SbNotification.error('Unable to load configuration', 'Please try again in a moment.');
      } finally {
        setIsLoading(false);
      }
    };
    loadConfiguration();
  }, []);

  const functionList = useMemo(
    () => (mergedCatalog ? Object.values(mergedCatalog) : []),
    [mergedCatalog],
  );

  const totalStats = useMemo(() => {
    let totalSelected = 0;
    let groupsWithSelection = 0;
    let totalCustom = 0;

    functionList.forEach((func) => {
      let funcHasSelection = false;
      func.categories.forEach((cat) => {
        const selected = selectedKeysByCategory[cat.category_key] ?? [];
        const tools = toolsByCategory[cat.category_key] ?? [];
        totalSelected += selected.length;
        totalCustom += tools.filter(
          (tool) => tool.is_custom && selected.includes(tool.tool_key),
        ).length;
        if (selected.length > 0) funcHasSelection = true;
      });
      if (funcHasSelection) groupsWithSelection += 1;
    });

    return { totalSelected, groupsWithSelection, totalCustom };
  }, [selectedKeysByCategory, toolsByCategory, functionList]);


  const handleToolToggle = (catKey, toolKey) => {
    setSelectedKeysByCategory((prev) => {
      const current = prev[catKey] ?? [];
      return {
        ...prev,
        [catKey]: current.includes(toolKey)
          ? current.filter((key) => key !== toolKey)
          : [...current, toolKey],
      };
    });
  };

  const handleAddCustomTool = (catKey) => {
    const value = getDraft(catKey).trim();
    if (!value) return;
    const newToolKey = generateKey(value);

    const existingTools = toolsByCategory[catKey] ?? [];
    const isDuplicate = existingTools.some(
      (tool) =>
        tool.tool_key === newToolKey ||
        tool.tool_name.trim().toLowerCase() === value.toLowerCase(),
    );

    if (isDuplicate) {
      SbNotification.warning('Duplicate entry', 'This tool already exists in the category.');
      return;
    }

    setToolsByCategory((prev) => ({
      ...prev,
      [catKey]: [...(prev[catKey] ?? []), { tool_key: newToolKey, tool_name: value, is_custom: true }],
    }));
    setSelectedKeysByCategory((prev) => ({
      ...prev,
      [catKey]: [...(prev[catKey] ?? []), newToolKey],
    }));
    clearDraft(catKey);
  };

  const handleRemoveCustomTool = (catKey, toolKey, toolName) => {
    let deleteConfirmText = '';
    let modalInstance;

    const handleInputChange = (e) => {
      deleteConfirmText = e.target.value;
      // Force re-render of modal buttons
      modalInstance.update({
        content: (
          <div>
            <p>Are you sure you want to delete "<strong>{toolName}</strong>"?</p>
            <p>Type <strong>DELETE</strong> to confirm:</p>
            <SbInput
              placeholder="Type DELETE to confirm"
              onChange={handleInputChange}
              autoFocus
              style={{ marginTop: '8px' }}
            />
          </div>
        ),
        okButtonProps: {
          disabled: deleteConfirmText !== 'DELETE',
        },
      });
    };

    modalInstance = SbModal.confirm({
      title: 'Confirm Delete',
      content: (
        <div>
          <p>Are you sure you want to delete "<strong>{toolName}</strong>"?</p>
          <p>Type <strong>DELETE</strong> in capitals to confirm:</p>
          <SbInput
            placeholder="Type DELETE to confirm"
            onChange={handleInputChange}
            autoFocus
            style={{ marginTop: '8px' }}
          />
        </div>
      ),
      okText: 'Delete',
      cancelText: 'Cancel',
      okButtonProps: {
        disabled: true,
        danger: true,
      },
      onOk: () => {
        setToolsByCategory((prev) => ({
          ...prev,
          [catKey]: (prev[catKey] ?? []).filter((tool) => tool.tool_key !== toolKey),
        }));
        setSelectedKeysByCategory((prev) => ({
          ...prev,
          [catKey]: (prev[catKey] ?? []).filter((key) => key !== toolKey),
        }));
      },
    });
  };

  const handleReset = () => {
    if (!savedSnapshot) return;
    setToolsByCategory(deepCloneState(savedSnapshot.tools));
    setSelectedKeysByCategory(deepCloneState(savedSnapshot.selected));
    clearAllDrafts();
  };

  // Reshape flat state back into the nested payload the API expects.
  // Master-tool selections and custom entries go into separate top-level keys.
  const buildSavePayload = () => {
    const selected_tools = {};
    const custom_entries = [];

    functionList.forEach((func) => {
      const selectedCategories = [];

      func.categories.forEach((cat) => {
        const catKey = cat.category_key;
        const tools = toolsByCategory[catKey] ?? [];
        const selectedSet = new Set(selectedKeysByCategory[catKey] ?? []);

        tools
          .filter((tool) => tool.is_custom && selectedSet.has(tool.tool_key))
          .forEach((tool) => {
            custom_entries.push({
              function_key: func.function_key,
              category_key: catKey,
              entry_key: tool.tool_key,
              entry_name: tool.tool_name,
            });
          });

        const selectedMasterTools = tools
          .filter((tool) => !tool.is_custom && selectedSet.has(tool.tool_key))
          .map((tool) => ({ tool_key: tool.tool_key, tool_name: tool.tool_name }));

        if (selectedMasterTools.length > 0) {
          selectedCategories.push({
            category_key: catKey,
            category_name: cat.category_name,
            selected_tools: selectedMasterTools,
          });
        }
      });

      if (selectedCategories.length > 0) {
        selected_tools[func.function_key] = {
          function_key: func.function_key,
          function_name: func.function_name,
          categories: selectedCategories,
        };
      }
    });

    return { selected_tools, custom_entries };
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveRegistryManagement(buildSavePayload());
      setSavedSnapshot({
        tools: deepCloneState(toolsByCategory),
        selected: deepCloneState(selectedKeysByCategory),
      });
      SbNotification.success('Configuration saved', 'Organisation technology context has been updated.');
    } catch {
      SbNotification.error('Save failed', 'Unable to save organisation technology context.');
    } finally {
      setIsSaving(false);
    }
  };

  const collapseItems = functionList.map((func, index) => {
    const categoryCount = func.categories.length;
    const selectedCount = func.categories.reduce(
      (sum, cat) => sum + (selectedKeysByCategory[cat.category_key] ?? []).length,
      0,
    );

    return {
      key: func.function_key,
      label: (
        <Flex flex={1} align="center" justify="space-between">
          <Flex align="center" gap={12}>
            <FunctionAvatarStyled $index={index}>
              {getAvatarInitials(func.function_name)}
            </FunctionAvatarStyled>
            <Text strong>{func.function_name}</Text>
          </Flex>
          <Text type="secondary">
            {categoryCount} {categoryCount === 1 ? 'category' : 'categories'} · {selectedCount} selected
          </Text>
        </Flex>
      ),
      children: func.categories.map((cat) => {
        const catKey = cat.category_key;
        const tools = toolsByCategory[catKey] ?? [];
        const selected = selectedKeysByCategory[catKey] ?? [];

        return (
          <CategorySectionStyled key={catKey}>
            <SbDivider orientation="left" plain>
              <Text type="secondary">{cat.category_name}</Text>
            </SbDivider>

            {tools.length > 0 && (
              <SbRow gutter={[16, 12]}>
                {tools.map((tool) => (
                  <SbCol key={tool.tool_key} xs={24} sm={12} md={8} lg={6}>
                    <Flex align="center" gap={6}>
                      <SbTooltip content={TOOLTIP_SELECT_TOOL}>
                        <SbCheckbox
                          checked={selected.includes(tool.tool_key)}
                          onChange={() => handleToolToggle(catKey, tool.tool_key)}
                        >
                          {tool.tool_name}
                        </SbCheckbox>
                      </SbTooltip>
                      {tool.is_custom && (
                        <DeleteIconStyled
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveCustomTool(catKey, tool.tool_key, tool.tool_name);
                          }}
                        >
                          <CloseOutlined />
                        </DeleteIconStyled>
                      )}
                    </Flex>
                  </SbCol>
                ))}
              </SbRow>
            )}

            <Permission resource="org_tech_context_setup" actions={["edit"]}>
              <InputWrapperStyled>
                <SbTooltip content={TOOLTIP_SEARCH_TOOLS}>
                  <div>
                    <InlineAddInput
                      placeholder="Other (specify) — press Enter to add"
                      value={getDraft(catKey)}
                      onChange={(value) => setDraft(catKey, value)}
                      onSubmit={() => handleAddCustomTool(catKey)}
                    />
                  </div>
                </SbTooltip>
              </InputWrapperStyled>
            </Permission>
          </CategorySectionStyled>
        );
      }),
    };
  });

  const hasNoData = !isLoading && functionList.length === 0;

  return (
    <SbSpin spinning={isLoading}>
      <PageWrapperStyled>
        <BackHeaderStyled align="center" gap={12}>
          <SbButton
            buttonType={BUTTON_TYPE.SECONDARY}
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/admin-setup')}
            aria-label="Back to Admin Setup"
          />
          <Title level={5}>Org Tech Context Setup</Title>
        </BackHeaderStyled>
        {hasNoData ? (
          <Flex vertical align="center" justify="center" gap={12}>
            <Title level={3}>No data available</Title>
            <Text type="secondary">Please check back later or contact support</Text>
          </Flex>
        ) : (
          <>
            <StatsBarStyled align="center" wrap="wrap" gap={8} justify="space-between">
              <Flex align="center" gap={8}>
                <SbTag color="blue">{totalStats.totalSelected} systems selected</SbTag>
                <SbTag>{totalStats.groupsWithSelection} of {functionList.length} groups</SbTag>
                <SbTag color="gold">{totalStats.totalCustom} custom entries</SbTag>
              </Flex>
              <Flex align="center" gap={8}>
                <ExpandCollapseButtons
                  expandAll={() => expandAll(functionList)}
                  collapseAll={collapseAll}
                />
                <SbButton
                  buttonType={BUTTON_TYPE.RED}
                  onClick={handleReset}
                  disabled={isLoading || isSaving}
                >
                  Reset
                </SbButton>
              </Flex>
            </StatsBarStyled>

            <SbCollapse
              activeKey={expandedKeys}
              onChange={setExpandedKeys}
              items={collapseItems}
            />
          </>
        )}

        {!hasNoData && (
          <FooterStyled>
            <Permission resource="org_tech_context_setup" actions={["edit"]}>
              <SbButton buttonType={BUTTON_TYPE.PRIMARY} onClick={handleSave} loading={isSaving}>
                Save
              </SbButton>
            </Permission>
          </FooterStyled>
        )}
      </PageWrapperStyled>
    </SbSpin>
  );
};

export default OrgTechContextSetup;
